import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Button,
  ButtonToolbar,
  Checkbox,
  FlexboxGrid,
  Form,
  SelectPicker,
} from "rsuite";

import "./ChartFilters.css";
import { updateIn } from "json-immut";
import { HelperServices } from "../../../core/services/HelperServices";
import { get } from "lodash/fp";
import { ChartListParams } from "./ChartDataTypes";
import {
  chartAvailabilityObjArr,
  messageTypeArr,
  roleTypeArr,
  statusObjArr,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { StoreServices } from "../../../core/services/StoreServices";
import { ChartServices } from "../../../core/services/ChartServices";
import { SongListParams } from "../Songs/SongDataTypes";

export interface PropTypesInterface {
  onSubmit: Function;
  onClear: Function;
  musicScheduleData?: any;
  showOutChartFilters?: boolean;
  showHeader?: boolean;
}

export default function ChartFiltersForMusicSchedule({
  onSubmit,
  onClear,
  musicScheduleData,
  showOutChartFilters = false,
  showHeader = true,
}: PropTypesInterface) {
  const { t } = useTranslation();
  const compId = musicScheduleData?.company_id;
  const storeGroupId = musicScheduleData?.store_group_id;
  const licenseId = musicScheduleData?.company?.active_contract?.music_licenses;
  const scheduleParams = {
    license: licenseId,
    include_store_group: storeGroupId,
    company: compId,
  };
  const initSearchFormData = {
    chart: "",
    license_id: licenseId,
    status: "",
    smart_search: "",
  };
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<SongListParams>(initSearchFormData);
  const company = useSelector((state: any) => state.company);
  const [selectedChart, setSelectedChart] = useState<number>(null);
  const [popularChartList, setPopularChartList] = useState([]);
  const [eventChartList, setEventChartList] = useState([]);
  const [masterChartList, setMasterChartList] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const handleSearchToggle = () => setShowFilter(!showFilter);
  const isBackendUser =
    UtilityServices.getLoggedInUser("user_type") == roleTypeArr.backend;

  function handleFormChanges(data: any) {
    let form = updateIn(formData, [data.path], data.value);
    setFormData(form);
  }

  async function init() {
    try {
      searchPopularChart();
      searchEventChart();
      searchMasterChart();
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    init();
  }, []);

  async function searchPopularChart(searchChart: string = "") {
    setPopularChartList([]);
    let params: any = {
      ...scheduleParams,
      for: isBackendUser ? "company" : "master, company",
      type: "popular, custom",
      smart_search: searchChart,
    };

    try {
      const res = await ChartServices.getCharts(params);
      const respData = get("data.data", res);
      if (respData.length) {
        setPopularChartList(respData);
      }
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function searchEventChart(searchChart: string = "") {
    setEventChartList([]);
    let params: any = {
      ...scheduleParams,
      for: isBackendUser ? "master, company" : "master, company",
      type: "event",
      smart_search: searchChart,
    };

    try {
      const res = await ChartServices.getCharts(params);
      const respData = get("data.data", res);
      if (respData.length) {
        setEventChartList(respData);
      }
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  async function searchMasterChart(searchChart: string = "") {
    setMasterChartList([]);
    if (!isBackendUser) return;
    let params: any = {
      for: "master",
      type: "popular, custom",
      license: licenseId,
      smart_search: searchChart,
    };

    try {
      const res = await ChartServices.getCharts(params);
      const respData = get("data.data", res);
      if (respData.length) {
        setMasterChartList(respData);
      }
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  function clearSearch() {
    setFormData(initSearchFormData);
    onClear && onClear();
  }

  return (
    <div>
      {showHeader && (
        <div className="row mb-2">
          <ButtonToolbar>
            <Button onClick={handleSearchToggle}>
              {" "}
              {t("CHART")} {t("ADVANCE_SEARCH")}
            </Button>
          </ButtonToolbar>
        </div>
      )}

      {(showFilter || showOutChartFilters) && (
        <div className="content mb-3 pb-0">
          <Form
            formValue={formData}
            onChange={(data: any) => {
              let form = { ...formData, ...data };
              setFormData(form);
            }}
            layout="inline"
          >
            <FlexboxGrid justify="space-between">
              <FlexboxGrid.Item>
                <Form.Group controlId="popular_chart_id">
                  <Form.ControlLabel>
                    {t("POPULAR")} {t("CHART")}
                  </Form.ControlLabel>
                  <SelectPicker
                    data={popularChartList}
                    onSearch={(e: any) => {
                      if (e) {
                        searchPopularChart(e);
                      }
                    }}
                    valueKey="id"
                    labelKey="chart_title"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "chart", value });
                      setSelectedChart(value);
                      onSubmit({ chart: value });
                    }}
                    onClean={(e: any) => {
                      handleFormChanges({ path: "chart", value: "" });
                      setSelectedChart(null);
                      onSubmit({ chart: null });
                    }}
                    name="popular_chart_id"
                    value={selectedChart}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <Form.Group controlId="event_chart_id">
                  <Form.ControlLabel>
                    {t("EVENT")} {t("CHART")}
                  </Form.ControlLabel>
                  <SelectPicker
                    data={eventChartList}
                    onSearch={(e: any) => {
                      if (e) {
                        searchEventChart(e);
                      }
                    }}
                    valueKey="id"
                    labelKey="chart_title"
                    onSelect={(value, item) => {
                      handleFormChanges({ path: "chart", value });
                      setSelectedChart(value);
                      onSubmit({ chart: value });
                    }}
                    onClean={(e: any) => {
                      handleFormChanges({ path: "chart", value: "" });
                      setSelectedChart(null);
                      onSubmit({ chart: null });
                    }}
                    name="event_chart_id"
                    value={selectedChart}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              {isBackendUser && (
                <FlexboxGrid.Item>
                  <Form.Group controlId="master_chart_id">
                    <Form.ControlLabel>
                      {t("MASTER")} {t("CHART")}
                    </Form.ControlLabel>
                    <SelectPicker
                      data={masterChartList}
                      onSearch={(e: any) => {
                        if (e) {
                          searchMasterChart(e);
                        }
                      }}
                      valueKey="id"
                      labelKey="chart_title"
                      onSelect={(value, item) => {
                        handleFormChanges({ path: "chart", value });
                        setSelectedChart(value);
                        onSubmit({ chart: value });
                      }}
                      onClean={(e: any) => {
                        handleFormChanges({ path: "chart", value: "" });
                        setSelectedChart(null);
                        onSubmit({ chart: null });
                      }}
                      name="master_chart_id"
                      value={selectedChart}
                    />
                  </Form.Group>
                </FlexboxGrid.Item>
              )}
            </FlexboxGrid>
            <div className="filter-btn">
              <Button onClick={() => clearSearch()}>{t("CLEAR_FILTER")}</Button>
              <Button onClick={() => onSubmit(formData)}>{t("Search")}</Button>
            </div>
          </Form>
        </div>
      )}
    </div>
  );
}
